// Custom hook for drag and drop functionality
import { useState, useCallback } from 'react';
import { AudioFile, Folder } from '@types';

interface UseDragAndDropReturn {
  draggedFile: AudioFile | null;
  draggedFolder: Folder | null;
  isDragging: boolean;
  handleDragStart: (file: AudioFile) => void;
  handleFolderDragStart: (folder: Folder) => void;
  handleDragEnd: () => void;
  handleDragOver: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent, targetFolderId: string | null) => void;
  handleDropOnEmpty: (e: React.DragEvent) => void;
}

export const useDragAndDrop = (
  onFileDrop: (fileId: string, targetFolderId: string | null) => void,
  onFolderDrop?: (folderId: string, targetFolderId: string | null) => void,
  onFileDropOnEmpty?: (file: AudioFile) => void
): UseDragAndDropReturn => {
  const [draggedFile, setDraggedFile] = useState<AudioFile | null>(null);
  const [draggedFolder, setDraggedFolder] = useState<Folder | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = useCallback((file: AudioFile) => {
    setDraggedFile(file);
    setDraggedFolder(null);
    setIsDragging(true);
  }, []);

  const handleFolderDragStart = useCallback((folder: Folder) => {
    setDraggedFolder(folder);
    setDraggedFile(null);
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedFile(null);
    setDraggedFolder(null);
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, targetFolderId: string | null) => {
      e.preventDefault();
      e.stopPropagation();

      if (draggedFile) {
        onFileDrop(draggedFile.id, targetFolderId);
      } else if (draggedFolder && onFolderDrop) {
        // Prevent dropping folder into itself or its descendants
        if (draggedFolder.id === targetFolderId) {
          return;
        }
        onFolderDrop(draggedFolder.id, targetFolderId);
      }

      setDraggedFile(null);
      setDraggedFolder(null);
      setIsDragging(false);
    },
    [draggedFile, draggedFolder, onFileDrop, onFolderDrop]
  );

  const handleDropOnEmpty = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (draggedFile && onFileDropOnEmpty) {
        onFileDropOnEmpty(draggedFile);
      }
      setDraggedFile(null);
      setDraggedFolder(null);
      setIsDragging(false);
    },
    [draggedFile, onFileDropOnEmpty]
  );

  return {
    draggedFile,
    draggedFolder,
    isDragging,
    handleDragStart,
    handleFolderDragStart,
    handleDragEnd,
    handleDragOver,
    handleDrop,
    handleDropOnEmpty,
  };
};

